import React, { useState, useCallback } from "react";
import { useIsMobile } from "./Hooks/useIsMobile";
import MobileTabBar, { MobileTab } from "./MobileTabBar";
import "./CSS/MobileLayout.css";

interface MobileLayoutProps {
  /**
   * The chat panel component (DocumentAgentChat)
   */
  chatPanel: React.ReactNode;
  /**
   * The document panel component (PaneManager or similar)
   */
  documentPanel: React.ReactNode;
  /**
   * Default tab to show on mobile. Defaults to "chat".
   */
  defaultTab?: MobileTab;
  /**
   * Desktop layout content (the original side-by-side layout).
   * Rendered as-is on desktop screens.
   */
  children: React.ReactNode;
  /**
   * Whether to show the counsel review tab (only for QandA Agent)
   */
  showCounselReview?: boolean;
  /**
   * Callback when counsel review tab is clicked.
   */
  onCounselReviewClick?: () => void;
  /**
   * The counsel panel component (form document for outside counsel)
   */
  counselPanel?: React.ReactNode;
  /**
   * Whether there are documents to show (if false, hides documents tab)
   */
  hasDocuments?: boolean;
  /**
   * Number of documents to display as a badge on the Documents tab
   */
  documentCount?: number;
}

/**
 * Wrapper component that provides mobile-responsive layout for Document Agent views.
 *
 * On mobile (<=576px): Shows either Chat or Documents panel full-screen with bottom tab bar.
 * On desktop (>576px): Renders children (original side-by-side layout) unchanged.
 */
const MobileLayout: React.FC<MobileLayoutProps> = ({
  chatPanel,
  documentPanel,
  defaultTab = "chat",
  children,
  showCounselReview = false,
  onCounselReviewClick,
  counselPanel,
  hasDocuments = true,
  documentCount,
}) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<MobileTab>(defaultTab);

  // Handler for counsel review click
  const handleCounselReviewClick = useCallback(() => {
    onCounselReviewClick?.();
  }, [onCounselReviewClick]);

  // Desktop: render original layout unchanged
  if (!isMobile) {
    return <>{children}</>;
  }

  // Mobile: render full-screen panel + bottom tab bar
  return (
    <div className="mobile-layout">
      <div className="mobile-layout__content">
        {activeTab === "chat" && (
          <div
            id="chat-panel"
            className="mobile-layout__panel mobile-layout__panel--chat"
            role="tabpanel"
            aria-labelledby="chat-tab"
          >
            {chatPanel}
          </div>
        )}
        {activeTab === "documents" && (
          <div
            id="documents-panel"
            className="mobile-layout__panel mobile-layout__panel--documents"
            role="tabpanel"
            aria-labelledby="documents-tab"
          >
            {documentPanel}
          </div>
        )}
        {activeTab === "counsel" && counselPanel && (
          <div
            id="counsel-panel"
            className="mobile-layout__panel mobile-layout__panel--counsel"
            role="tabpanel"
            aria-labelledby="counsel-tab"
          >
            {counselPanel}
          </div>
        )}
      </div>
      <MobileTabBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showCounselReview={showCounselReview}
        onCounselReviewClick={handleCounselReviewClick}
        hasDocuments={hasDocuments}
        documentCount={documentCount}
      />
    </div>
  );
};

export default MobileLayout;
